/**
 * Mixed case bottle selector
 *
 * Used by the `mixed_case` product block (see snippets/product-block-mixed-case.liquid).
 * Lets a customer split a fixed-size case between varieties. The chosen counts are
 * written into the product form as line item properties, so there is no need for the
 * customer to leave a note on the order.
 *
 * Only runs on pages that render a <mixed-case-selector> element, so no other product
 * template is affected.
 */
(function () {
  if (window.customElements && customElements.get('mixed-case-selector')) return;

  var selectors = {
    productContainer: '[data-section-type="product"]',
    form: '[data-product-form]',
    variantSelect: '[data-variant-select]',
    addToCart: '[data-add-to-cart]',
    group: '[data-mixed-case-group]',
    input: '[data-mixed-case-input]',
    step: '[data-mixed-case-step]',
    counter: '[data-mixed-case-counter]',
    message: '[data-mixed-case-message]',
    properties: '[data-mixed-case-properties]'
  };

  function toInt(value) {
    var parsed = parseInt(value, 10);
    return isNaN(parsed) ? 0 : parsed;
  }

  function pluralize(count, singular, plural) {
    return count === 1 ? singular : plural;
  }

  class MixedCaseSelectorElement extends HTMLElement {
    connectedCallback() {
      this.container = this.closest(selectors.productContainer) || document;
      this.form = this.container.querySelector(selectors.form);
      this.total = toInt(this.dataset.total) || 12;
      this.groups = Array.prototype.slice.call(this.querySelectorAll(selectors.group));
      this.counter = this.querySelector(selectors.counter);
      this.message = this.querySelector(selectors.message);

      if (!this.groups.length) return;

      this.strings = {
        counter: this.dataset.textCounter || '[count] of [total] bottles chosen',
        add: this.dataset.textAdd || 'Add [count] more [bottles] to complete the case.',
        remove: this.dataset.textRemove || 'Remove [count] [bottles] to complete the case.',
        complete: this.dataset.textComplete || 'Your case is complete.'
      };

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);

      this.addEventListener('click', this.handleClick);
      this.addEventListener('input', this.handleChange);
      this.addEventListener('change', this.handleChange);
      document.addEventListener('product:variant-change', this.handleVariantChange);
      // Capture phase on the document so an incomplete case is blocked before the
      // theme's own submit handler adds the item to the cart.
      document.addEventListener('submit', this.handleSubmit, true);

      this.setActiveGroup(this.currentVariantId());
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('input', this.handleChange);
      this.removeEventListener('change', this.handleChange);
      document.removeEventListener('product:variant-change', this.handleVariantChange);
      document.removeEventListener('submit', this.handleSubmit, true);
    }

    currentVariantId() {
      var select = this.form && this.form.querySelector(selectors.variantSelect);
      if (select && select.value) return select.value;
      return this.container.dataset ? this.container.dataset.currentProductId : null;
    }

    setActiveGroup(variantId) {
      var needle = ',' + variantId + ',';
      var match = null;

      this.groups.forEach(function (group) {
        if (!match && variantId && (group.dataset.variantIds || '').indexOf(needle) !== -1) {
          match = group;
        }
      });

      if (!match) {
        match = this.querySelector('[data-mixed-case-fallback]') || this.groups[0];
      }

      this.activeGroup = match;
      this.groups.forEach(function (group) {
        var isActive = group === match;
        group.hidden = !isActive;
        // Values of hidden groups must not end up in the form.
        Array.prototype.forEach.call(group.querySelectorAll(selectors.input), function (input) {
          input.disabled = !isActive;
        });
      });

      this.sync();
    }

    inputs() {
      if (!this.activeGroup) return [];
      return Array.prototype.slice.call(this.activeGroup.querySelectorAll(selectors.input));
    }

    selectedCount() {
      return this.inputs().reduce(function (sum, input) {
        return sum + toInt(input.value);
      }, 0);
    }

    isComplete() {
      return this.selectedCount() === this.total;
    }

    handleClick(event) {
      var button = event.target.closest(selectors.step);
      if (!button || !this.contains(button)) return;

      event.preventDefault();

      var input = button.parentElement.querySelector(selectors.input);
      if (!input) return;

      var next = toInt(input.value) + toInt(button.dataset.mixedCaseStep);
      input.value = Math.min(Math.max(next, 0), this.total);
      this.sync();
    }

    handleChange(event) {
      if (!event.target.matches(selectors.input)) return;

      // Clamp on `change` only, so a half-typed value isn't rewritten mid-keystroke.
      if (event.type === 'change') {
        var value = toInt(event.target.value);
        event.target.value = Math.min(Math.max(value, 0), this.total);
      }

      this.sync();
    }

    handleVariantChange(event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;
      this.setActiveGroup(String(variant.id));
    }

    handleSubmit(event) {
      if (!this.form || event.target !== this.form) return;
      if (this.isComplete()) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      this.classList.add('mixed-case--error');
      if (this.message) this.message.setAttribute('aria-live', 'assertive');
      if (typeof this.scrollIntoView === 'function') {
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    sync() {
      var count = this.selectedCount();
      var complete = count === this.total;
      var difference = Math.abs(this.total - count);

      if (this.counter) {
        this.counter.textContent = this.strings.counter
          .replace('[count]', count)
          .replace('[total]', this.total);
      }

      if (this.message) {
        var template = complete
          ? this.strings.complete
          : count < this.total
          ? this.strings.add
          : this.strings.remove;

        this.message.textContent = template
          .replace('[count]', difference)
          .replace('[bottles]', pluralize(difference, 'bottle', 'bottles'));
      }

      this.classList.toggle('mixed-case--incomplete', !complete);
      if (complete) this.classList.remove('mixed-case--error');

      var button = this.container.querySelector(selectors.addToCart);
      if (button) button.classList.toggle('mixed-case-blocked', !complete);

      this.updateProperties();
    }

    updateProperties() {
      if (!this.form) return;

      var holder = this.form.querySelector(selectors.properties);
      if (!holder) {
        holder = document.createElement('div');
        holder.setAttribute('data-mixed-case-properties', '');
        holder.hidden = true;
        this.form.appendChild(holder);
      }

      holder.innerHTML = '';

      if (!this.isComplete()) return;

      this.inputs().forEach(function (input) {
        var quantity = toInt(input.value);
        if (quantity <= 0) return;

        var property = document.createElement('input');
        property.type = 'hidden';
        property.name = 'properties[' + input.dataset.variety + ']';
        property.value = String(quantity);
        holder.appendChild(property);
      });
    }
  }

  customElements.define('mixed-case-selector', MixedCaseSelectorElement);
})();
